/**
 * Maintenance: the four things that have to run INSIDE the mail container.
 *
 * Checking a password against Dovecot, reloading Dovecot, making a DKIM key, and
 * deleting the maildir an account left behind. Everything else on the Server
 * screen is a config file the warden renders; these cannot be, so they go down a
 * named pipe to `scripts/dms-ctl.sh` (`apps/warden/src/ctl.ts` says why a pipe
 * and not a docker socket).
 *
 * THE ROUTE WAS BUILT END TO END AND NOTHING CALLED IT. `POST
 * /admin/warden/ctl`, the warden's handler, the pipe and the script all existed
 * and were tested, and no screen reached them — so reloading Dovecot or making a
 * key still meant a shell in the container.
 *
 * NO PLAN AND NO DIFF, unlike every form beside it. There is no file to show,
 * and "this will reload Dovecot" is a sentence rather than evidence. What the
 * destructive one gets instead is what the warden gives it: a fresh sign-in the
 * WARDEN asks for — this side sends and reads `reauth_required`, for any verb,
 * rather than keeping its own list — and the warden's host-sent alert.
 *
 * EVERY ANSWER IS READ OUT OF THE BODY. A 200 from `pw.verify` means the check
 * ran, not that the password is right — `result` is "yes" or "no" — and a 200
 * from `dovecot.reload` whose result is not "reloaded" is not a reload.
 *
 * ONE COMMAND AT A TIME, guarded by a ref that is set before the request leaves.
 * The warden serialises them anyway; what this stops is a double press sending
 * two, because `setState` is not readable on the next line and two submits in
 * one burst would both see `busy` still false. `busy` exists for rendering only.
 */
import { useRef, useState } from 'react'
import { api, errorText, HearthError } from './api.ts'
import { ReauthForm } from './ReauthForm.tsx'

/** The warden's `CTL_VERBS`, as the helper that sends them spells the type. */
type CtlVerb = Parameters<typeof api.wardenCtl>[0]

export interface ServerControlsProps {
  /**
   * `ctl` from the warden's `/health`, as it arrived.
   *
   * THREE VALUES AND TWO OUTCOMES. `false` is a working installation without the
   * pipe, and the forms are replaced by a sentence saying so. `undefined` is an
   * origin that does not say yet, or an answer still on its way — and those show
   * the forms, because the warden refuses what it cannot run and says why.
   * Hiding four controls on a guess would be deciding something this side does
   * not know.
   *
   * READ ON EVERY RENDER, never copied into state: `/health` answers after this
   * mounts, and a copy taken at mount is a copy of "not yet".
   */
  readonly ctl: boolean | undefined
  /** Ask `/health` again — for when the warden says it has no pipe at all. */
  readonly refreshHealth: () => void
  /** For a refused re-auth token, which is not any one verb's answer. */
  readonly onError: (message: string) => void
}

/** What a verb's card says once the warden has answered. */
interface Said {
  readonly tone: 'done' | 'no'
  /** One sentence for a person. */
  readonly text: string
  /** The warden's own sentence, when `text` is ours. */
  readonly detail?: string
  /** Output a tool printed, as it came, less terminal colour. */
  readonly output?: string
  /** Every DNS record lifted out of that output, to copy. */
  readonly records?: readonly DkimRecord[]
}

interface DkimRecord {
  /** `<selector>._domainkey.<domain>`, when the output named it. */
  readonly name: string | null
  readonly value: string
}

/** Where a command's outcome is unknown. NOT "you can ask again": a command that timed out can still have run. */
const NO_ANSWER = 'No answer within 30 s. It may still have run — check before trying again.'

/**
 * What pressing "Make the key" does to a key the domain already has — nothing,
 * and the evidence is docker-mailserver's own source at v16.0.0.
 *
 * `setup config dkim` runs `open-dkim` (`target/bin/setup` line 135), which
 * hands off to `rspamd-dkim` when rspamd is on (`target/bin/open-dkim` lines
 * 6-10) — the setup this installation runs; the runbook's Change 8 keeps the key
 * under `rspamd/dkim/`. `target/bin/rspamd-dkim` lines 178-181 then refuse with
 * "Not overwriting existing files (use '--force' to overwrite existing files)"
 * and exit 1 when that key's files exist, and `dms-ctl.sh` never passes
 * `--force`. The OpenDKIM path skips an existing key instead (`open-dkim` line
 * 190). So this is not a destructive verb and the warden does not ask for a
 * second sign-in; the refusal arrives below in the container's own words.
 */
const DKIM_NOTE =
  'If the domain already has a key, docker-mailserver refuses rather than replace it, and what it said appears here. ' +
  'Publish the record it prints before relying on the key.'

/**
 * Terminal colour, taken out.
 *
 * docker-mailserver's `_log` wraps each level name in `ESC[0;34m … ESC[0m`, and
 * `dms-ctl` hands back stdout and stderr together, so that output arrives
 * carrying escapes a browser would print as `[0;34m`. Only CSI sequences are
 * removed; every character around them is the tool's own. Spelled `\x1b`
 * because a literal escape is a control character in source, which
 * `control-characters.test.ts` refuses.
 */
const ANSI = /\x1b\[[0-9;?]*[ -/]*[@-~]/g
const withoutAnsi = (text: string): string => text.replace(ANSI, '')

const asText = (value: unknown): string => (typeof value === 'string' ? value : (JSON.stringify(value) ?? String(value)))

/** HH:MM on this browser's clock, from milliseconds. */
const hhmm = (ms: number): string => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

/**
 * The TXT record in `setup config dkim`'s output, if it printed one.
 *
 * FOUND BY WHAT IT IS, NOT WHERE IT IS. v16's `rspamd-dkim` prints "Here is the
 * content of the TXT DNS record mail._domainkey.<domain> that you need to
 * create:" and then the record on a line of its own (lines 268-269); the
 * OpenDKIM path prints none. That wording is not a contract, so this looks for
 * what a DKIM key record must contain — `v=DKIM1` — and for a `._domainkey.`
 * name anywhere, and adds nothing when there is none. The whole output is on
 * screen either way.
 */
const DKIM_NAME = /([a-z0-9][a-z0-9_.-]*\._domainkey\.[a-z0-9][a-z0-9.-]*[a-z0-9])/i

function dkimRecordsIn(output: string, domain: string): DkimRecord[] {
  const lines = output.split('\n')
  const suffix = `._domainkey.${domain.trim().toLowerCase()}`
  const records: DkimRecord[] = []
  // Where this record's own lines start: after the previous record, so a name
  // cannot be borrowed from the one above it.
  let from = 0
  lines.forEach((line, index) => {
    if (!line.includes('v=DKIM1')) return
    // A zone-file line carries the record in quoted chunks, `( "v=DKIM1; k=rsa; " "p=MIIB…" )`,
    // and the record is those chunks joined — which is what `rspamd-dkim` line 264 does too.
    const quoted = [...line.matchAll(/"([^"]*)"/g)].map((match) => match[1] ?? '').join('')
    const value = (quoted.includes('v=DKIM1') ? quoted : line.slice(line.indexOf('v=DKIM1'))).trim()
    let name: string | null = null
    for (let at = index; at >= from && name === null; at -= 1) {
      const found = DKIM_NAME.exec(lines[at] ?? '')?.[1] ?? null
      if (found !== null && found.toLowerCase().endsWith(suffix)) name = found
    }
    records.push({ name, value })
    from = index + 1
  })
  return records
}

/**
 * What a refusal says, in the warden's own words wherever it has some.
 *
 * `ctl_failed` IS THE SCRIPT'S SENTENCE, shown as it came: "no maildir for …",
 * doveadm's output, DMS's refusal to overwrite a key. A rewording here would be
 * a second author of an error this side cannot see the cause of.
 */
function refusalOf(error: unknown): Said {
  if (!(error instanceof HearthError)) return { tone: 'no', text: errorText(error, 'That did not reach the server.') }
  const body = error.body as { retryable?: unknown; refusals?: readonly { field: string; reason: string }[] } | null
  if (error.code === 'ctl_unavailable' && body?.retryable === true) {
    return { tone: 'no', text: NO_ANSWER, detail: error.message }
  }
  if (error.code === 'ctl_failed') {
    return { tone: 'no', text: 'It did not work. What the mail container said:', output: withoutAnsi(error.message) }
  }
  if (Array.isArray(body?.refusals) && body.refusals.length > 0) {
    return { tone: 'no', text: body.refusals.map((r) => `${r.field}: ${r.reason}`).join(' · ') }
  }
  return { tone: 'no', text: error.message }
}

/**
 * Every record the run printed, not the first one.
 *
 * A DOMAIN CAN HAVE MORE THAN ONE SELECTOR — that is what selectors are for, and
 * what a rotation looks like halfway through — so showing one silently is hiding
 * the key somebody came here to publish.
 */
function RecordBoxes({ records }: { readonly records: readonly DkimRecord[] }): React.JSX.Element {
  return (
    <>
      {records.map((record, index) => (
        <div className="admin-secret" key={record.name ?? `record-${index}`}>
          {record.name !== null ? (
            <label className="admin-field">
              <span>Record name</span>
              <input readOnly value={record.name} spellCheck={false} />
            </label>
          ) : null}
          {/* A TEXTAREA, because a 2048-bit key is one unbroken run of base64 and
              this class is the one that wraps anywhere. Read-only, so a click
              selects it rather than editing what gets pasted into DNS. */}
          <label className="admin-field">
            <span>Record value</span>
            <textarea className="admin-reauth-token" readOnly value={record.value} rows={4} spellCheck={false} />
          </label>
          <button
            type="button"
            className="admin-propose"
            // OPTIONAL-CHAINED: there is no clipboard API over plain HTTP, and the
            // value is selectable text either way.
            onClick={() => void navigator.clipboard?.writeText(record.value)}
          >
            Copy the value
          </button>
        </div>
      ))}
    </>
  )
}

function Answer({ said }: { readonly said: Said }): React.JSX.Element {
  return (
    <div role="status" className={said.tone === 'done' ? 'admin-applied' : 'admin-warning'}>
      <p>{said.text}</p>
      {said.detail ? <p className="admin-note">{said.detail}</p> : null}
      {said.records !== undefined && said.records.length > 0 ? <RecordBoxes records={said.records} /> : null}
      {said.output !== undefined ? (
        <div className="admin-diff">
          <pre>{said.output === '' ? '(no output)' : said.output}</pre>
        </div>
      ) : null}
    </div>
  )
}

type Reply = { readonly ok: true; readonly result: unknown } | { readonly ok: false }

export function ServerControls({ ctl, refreshHealth, onError }: ServerControlsProps): React.JSX.Element {
  const inFlight = useRef(false)
  const [busy, setBusy] = useState<CtlVerb | null>(null)
  const [said, setSaid] = useState<Partial<Record<CtlVerb, Said>>>({})
  /**
   * The verb the warden last wanted a fresh sign-in for, and — once it is had —
   * until when, in Unix SECONDS from the origin.
   *
   * THE GRANT DOES NOT PRESS THE BUTTON. Signing in again is the second half of
   * a decision to delete somebody's mail, and sending the delete the moment a
   * token verifies would make the token paste the decision. The card says the
   * grant stands and waits.
   */
  const [reauth, setReauth] = useState<{ readonly verb: CtlVerb; readonly because: string; readonly until: number | null } | null>(
    null,
  )
  const [pwEmail, setPwEmail] = useState('')
  const [pwPassword, setPwPassword] = useState('')
  const [domain, setDomain] = useState('')
  const [purgeEmail, setPurgeEmail] = useState('')
  const [purgeAgain, setPurgeAgain] = useState('')

  /** Replace one verb's answer, or clear it — called when its inputs change, so an answer never sits beside a question it was not for. */
  const show = (verb: CtlVerb, next: Said | null): void =>
    setSaid((current) => {
      const copy = { ...current }
      if (next === null) delete copy[verb]
      else copy[verb] = next
      return copy
    })

  /** Send one verb. Null means one is already out and nothing was sent. */
  const run = async (verb: CtlVerb, args: Record<string, unknown>): Promise<Reply | null> => {
    if (inFlight.current) return null
    inFlight.current = true
    setBusy(verb)
    try {
      const answer = await api.wardenCtl(verb, args)
      setReauth((current) => (current?.verb === verb ? null : current))
      return { ok: true, result: answer.result }
    } catch (error) {
      if (error instanceof HearthError && error.code === 'reauth_required') {
        show(verb, null)
        setReauth({ verb, because: error.message, until: null })
      } else {
        setReauth((current) => (current?.verb === verb ? null : current))
        show(verb, refusalOf(error))
        // NO PIPE AT ALL IS A STATE OF THE INSTALLATION, not of this command, so
        // `/health` is asked again and the panel follows whatever it says. The
        // warden marks it `retryable: false`; a timeout is `true` and is not this.
        const body = error instanceof HearthError ? (error.body as { retryable?: unknown } | null) : null
        if (error instanceof HearthError && error.code === 'ctl_unavailable' && body?.retryable !== true) refreshHealth()
      }
      return { ok: false }
    } finally {
      inFlight.current = false
      setBusy(null)
    }
  }

  const checkPassword = async (): Promise<void> => {
    const email = pwEmail.trim()
    if (email === '' || pwPassword === '') return
    const reply = await run('pw.verify', { email, password: pwPassword })
    if (reply === null) return
    // AFTER EVERY ANSWER, yes or no or a refusal: a password left in a field
    // on an admin screen is a password somebody else can reveal.
    setPwPassword('')
    if (!reply.ok) return
    show(
      'pw.verify',
      reply.result === 'yes'
        ? { tone: 'done', text: `Dovecot accepts that password for ${email}.` }
        : reply.result === 'no'
          ? // "no" IS EVERY NON-ZERO EXIT of `doveadm auth test` — a wrong
            // password, an unknown user, and doveadm missing all look alike
            // from here — so the sentence names all three rather than guess.
            { tone: 'no', text: 'Dovecot does not accept it — a wrong password, no such account, or the check could not run.' }
          : { tone: 'no', text: `Unexpected answer: ${asText(reply.result)}` },
    )
  }

  const reload = async (): Promise<void> => {
    const reply = await run('dovecot.reload', {})
    if (reply === null || !reply.ok) return
    show(
      'dovecot.reload',
      reply.result === 'reloaded'
        ? { tone: 'done', text: `Reloaded at ${hhmm(Date.now())}.` }
        : { tone: 'no', text: 'Unexpected answer:', output: withoutAnsi(asText(reply.result)) },
    )
  }

  const makeKey = async (): Promise<void> => {
    const asked = domain.trim()
    if (asked === '') return
    const reply = await run('dkim.keygen', { domain: asked })
    if (reply === null || !reply.ok) return
    const output = withoutAnsi(asText(reply.result))
    // THE DOMAIN THAT WAS ASKED FOR goes in, because a record's name is only its
    // name if it ends in that domain: output mentioning somebody else's
    // `_domainkey` host would otherwise label this key with a name that, once
    // published, leaves the real one unsigned.
    const records = dkimRecordsIn(output, asked)
    show(
      'dkim.keygen',
      records.length > 0
        ? {
            tone: 'done',
            text:
              records.length === 1
                ? `A key was made for ${asked}. Publish this TXT record in its DNS:`
                : `A key was made for ${asked}. Publish these ${records.length} TXT records in its DNS:`,
            output,
            records,
          }
        : { tone: 'done', text: 'docker-mailserver said:', output },
    )
  }

  const purge = async (): Promise<void> => {
    // WHAT WAS TYPED TWICE IS WHAT IS SENT, untrimmed: the confirmation is of
    // these characters, and the warden refuses whitespace in an address anyway.
    if (purgeEmail === '' || purgeEmail !== purgeAgain) return
    const email = purgeEmail
    const reply = await run('account.purgeMaildir', { email })
    if (reply === null || !reply.ok) return
    if (reply.result === 'purged') {
      setPurgeEmail('')
      setPurgeAgain('')
      show('account.purgeMaildir', { tone: 'done', text: `Deleted the mail of ${email}.` })
    } else {
      show('account.purgeMaildir', { tone: 'no', text: 'Unexpected answer:', output: withoutAnsi(asText(reply.result)) })
    }
  }

  const reauthFor = (verb: CtlVerb) => (reauth?.verb === verb ? reauth : null)
  /** While the form is up, the verb's own button is not: the form is the way forward. */
  const signingIn = (verb: CtlVerb): boolean => reauthFor(verb)?.until === null

  /** The second factor, in the card of the verb the warden asked it for. */
  const secondFactor = (verb: CtlVerb): React.JSX.Element | null => {
    const asked = reauthFor(verb)
    if (asked === null) return null
    if (asked.until === null) {
      return (
        <ReauthForm
          because={asked.because}
          onGranted={(expiresAt) =>
            setReauth((current) => (current?.verb === verb ? { ...current, until: expiresAt } : current))
          }
          onRefused={(error) => onError(errorText(error, 'That token was not accepted.'))}
        />
      )
    }
    return (
      <p className="admin-note" role="status">
        Signed in again until {hhmm(asked.until * 1000)}.
      </p>
    )
  }

  const answer = (verb: CtlVerb): React.JSX.Element | null => {
    const one = said[verb]
    return one ? <Answer said={one} /> : null
  }

  // SAID INSTEAD OF THE FORMS, and only on an explicit `false`: see `ctl` above.
  if (ctl === false) {
    return (
      <p className="admin-warning">
        No connection into the mail container, so these four can't run here. Everything else on this screen works.
        Setup: runbook Change 8, step 4 (<code>WARDEN_CTL_PATH</code>).
      </p>
    )
  }

  const idle = busy === null
  const purgeGranted = reauthFor('account.purgeMaildir')?.until != null

  // A SECTION PER VERB WITH THE FORM INSIDE IT, because the re-auth step is a
  // form of its own and a form cannot sit inside another.
  return (
    <div className="admin-forms">
      <section className="admin-form">
        <h3 className="admin-form-title">Check a password</h3>
        <form
          className="admin-form"
          onSubmit={(event) => {
            event.preventDefault()
            void checkPassword()
          }}
        >
          <label className="admin-field">
            <span>Address</span>
            <input
              value={pwEmail}
              placeholder="anna@h-group.net"
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => {
                setPwEmail(event.target.value)
                show('pw.verify', null)
              }}
            />
          </label>
          <label className="admin-field">
            <span>Password</span>
            <input
              type="password"
              value={pwPassword}
              autoComplete="off"
              onChange={(event) => {
                setPwPassword(event.target.value)
                show('pw.verify', null)
              }}
            />
          </label>
          <p className="admin-note">Asks Dovecot whether it would let this address sign in with this password. Nothing is changed.</p>
          {signingIn('pw.verify') ? null : (
            <button type="submit" className="admin-propose" disabled={!idle || pwEmail.trim() === '' || pwPassword === ''}>
              {busy === 'pw.verify' ? 'Checking…' : 'Check'}
            </button>
          )}
        </form>
        {secondFactor('pw.verify')}
        {answer('pw.verify')}
      </section>

      <section className="admin-form">
        <h3 className="admin-form-title">Reload Dovecot</h3>
        <p className="admin-note">Makes Dovecot read its configuration again. It asks nothing first.</p>
        {signingIn('dovecot.reload') ? null : (
          <button type="button" className="admin-propose" disabled={!idle} onClick={() => void reload()}>
            {busy === 'dovecot.reload' ? 'Reloading…' : 'Reload Dovecot'}
          </button>
        )}
        {secondFactor('dovecot.reload')}
        {answer('dovecot.reload')}
      </section>

      <section className="admin-form">
        <h3 className="admin-form-title">Make a DKIM key</h3>
        <form
          className="admin-form"
          onSubmit={(event) => {
            event.preventDefault()
            void makeKey()
          }}
        >
          <label className="admin-field">
            <span>Domain</span>
            <input
              value={domain}
              placeholder="h-group.net"
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => {
                setDomain(event.target.value)
                show('dkim.keygen', null)
              }}
            />
          </label>
          <p className="admin-note">{DKIM_NOTE}</p>
          {signingIn('dkim.keygen') ? null : (
            <button type="submit" className="admin-propose" disabled={!idle || domain.trim() === ''}>
              {busy === 'dkim.keygen' ? 'Making…' : 'Make the key'}
            </button>
          )}
        </form>
        {secondFactor('dkim.keygen')}
        {answer('dkim.keygen')}
      </section>

      <section className="admin-form">
        <h3 className="admin-form-title">Delete a removed account's mail</h3>
        <form
          className="admin-form"
          onSubmit={(event) => {
            event.preventDefault()
            void purge()
          }}
        >
          <label className="admin-field">
            <span>Address</span>
            <input
              value={purgeEmail}
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => {
                setPurgeEmail(event.target.value)
                show('account.purgeMaildir', null)
              }}
            />
          </label>
          <label className="admin-field">
            <span>Type the address again</span>
            <input
              value={purgeAgain}
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => {
                setPurgeAgain(event.target.value)
                show('account.purgeMaildir', null)
              }}
            />
          </label>
          <p className="admin-note">
            Removing an account leaves its mail on disk. This deletes that mail for good — there is no undo — and the
            server refuses while the account still exists.
          </p>
          {signingIn('account.purgeMaildir') ? null : (
            <button
              type="submit"
              className="admin-apply"
              disabled={!idle || purgeEmail === '' || purgeEmail !== purgeAgain}
            >
              {busy === 'account.purgeMaildir' ? 'Deleting…' : purgeGranted ? 'Delete the mail now' : 'Delete the mail'}
            </button>
          )}
        </form>
        {secondFactor('account.purgeMaildir')}
        {answer('account.purgeMaildir')}
      </section>
    </div>
  )
}
